import {createSlice, type PayloadAction} from "@reduxjs/toolkit";
import type ConstructionListItem from "./types/ConstructionListItem";
import {type ConstructionsNewState } from "./types/ConstructionsNewState";
import type IFiltersNew from "../../types/IFiltersNew";
import { filterData, filterDataWithPagination } from "../../utils/Pagination";
import {setToComboBoxOption} from "../../utils/ComboBoxMapping";
import { getConstructionsFilterData, updateStateCollections } from "./hooks/useSliceNewHook";
import {type IComboBoxOption } from "@fluentui/react";
import type ConstructionBulkEdit from "./types/ConstructionBulkEdit";
import type ConstructionLocationCodes from "./types/ConstructionLocationCodes";
import type IPayloadConstructionStateUpdate from "./types/IPayloadConstructionStateUpdate";
import {type TagConstruction} from "../../types/TagConstruction";

const initialState: ConstructionsNewState = {
  loading: true,
  all: [],
  page: [],
  master: [],
  tags: [],
  createdBy: [],
  locationCodes: [],
  constructionLocationCodes: undefined,
  constructionLocationTypes: undefined,
  filters: {
    name: undefined,
    from: undefined,
    to: undefined,
    tags: [],
    locationCodes: [],
    createdBy: undefined,
  },
  pagination: {
    currentPage: 0,
    pageSize: 15,
    total: 0,
  },
};

export const constructionsNewSlice = createSlice({
  name: "constructionsNew",
  initialState,
  reducers: {
    load: (
        state, 
        { payload }: PayloadAction<{ data: ConstructionListItem[], filters?: IFiltersNew, tags?: TagConstruction[] }>,
    ) => {
      state.loading = true;
      
      if (payload.filters) {
        state.filters = payload.filters;
      }

      state.master = payload.data;
      state.all = filterData(payload.data, state.filters) ?? [];
      state.page = filterDataWithPagination(payload.data, state.filters, state.pagination.pageSize, state.pagination.currentPage) ?? [];
      state.pagination.total = state.all.length;

      const [locationCodes, createdBy] = getConstructionsFilterData(state.master);

      state.tags = setToComboBoxOption(new Set((payload.tags ?? []).map(t => t.value)));
      state.locationCodes = locationCodes;
      state.createdBy = createdBy;

      state.loading = false;
    },

    select: (
      state, 
      { payload }: PayloadAction<ConstructionListItem[] | null>) => {
      if (payload === null) {
        state.page.forEach(item => {
          item.isSelected = false;
        });
        return;
      }

      state.page.forEach(item => {
        item.isSelected = payload.some(
          selectedItem => selectedItem.id === item.id,
        );
      });

      state.page.forEach(item => {
        if (!payload.some(selectedItem => selectedItem.id === item.id)) {
          item.isSelected = false;
        }
      });
    },

    search: (state, { payload }: PayloadAction<IFiltersNew>) => {
      state.loading = true;
      state.filters = payload;

      state.all = filterData(state.master, payload) ?? [];
      state.page = filterDataWithPagination(state.master, payload, state.pagination.pageSize, 0) ?? [];

      state.pagination.total = state.all.length;
      state.pagination.currentPage = 0;
      state.loading = false;
    },

    changePage: (state, action: PayloadAction<number>) => {
      state.pagination.currentPage = action.payload;
      state.page = filterDataWithPagination(state.master, state.filters, state.pagination.pageSize, action.payload) ?? [];
    },
     

    sort: (
      state,
      { payload }: PayloadAction<{ columnName: keyof ConstructionListItem; isSortedDescending: boolean }>
    ) => {
      const { columnName, isSortedDescending } = payload;
      
      state.page = state.page.sort((a, b) => {
        const aValue = a[columnName] ?? 0;
        const bValue = b[columnName] ?? 0;

        if (aValue < bValue) return isSortedDescending ? 1 : -1;
        if (aValue > bValue) return isSortedDescending ? -1 : 1;
        return 0;
     });
    },

    addTags: (
      state,
      {
        payload,
      }: PayloadAction<{tags: IComboBoxOption[]; constructionIds: string[]}>,
    ) => {
      const missingTags = payload.tags.filter(
        tag => state.tags.findIndex(t => t.key === tag.key) === -1,
      );
      const normalizedTags = missingTags.map(({key, text, data}) => ({
        key,
        text,
        data,
      }));
      state.tags = [...state.tags, ...normalizedTags];

      payload.constructionIds.forEach(constructionId => {
        [state.page, state.all, state.master].forEach(collection => {
          const constructionIndex = collection.findIndex(c => c.id === constructionId);
          if (constructionIndex !== -1) {
            payload.tags.forEach((tag: IComboBoxOption) => {
              const index = collection[constructionIndex].tags?.findIndex((t: string) => t === tag.key) ?? -1;
              if (index === -1) {
                collection[constructionIndex].tags?.push(String(tag.key));
              }
            });
          }
        });
      });
    },

    removeTagFromConstruction: (state, { payload }: PayloadAction<{ tag: string, constructionId: string }>) => {
      const index = state.page.findIndex(c => c.id === payload.constructionId);
      if (index !== -1) {
        state.page[index].tags = state.page[index].tags?.filter((t: string) => t !== payload.tag);
      }
    },

    removeTags: (
      state,
      {
        payload,
      }: PayloadAction<{tags: IComboBoxOption[]; constructionIds: string[]}>,
    ) => {
      payload.constructionIds.forEach(constructionId => {
        [state.page, state.all, state.master].forEach(collection => {
          const constructionIndex = collection.findIndex(c => c.id === constructionId);
          if (constructionIndex !== -1) {
            payload.tags.forEach((tag: IComboBoxOption) => {
              const index = collection[constructionIndex].tags?.findIndex((t: string) => t === tag.key) ?? -1;
              if (index !== -1) {
                collection[constructionIndex].tags?.splice(index, 1);
              }
            });
          }
        });

        payload.tags.forEach((tag: IComboBoxOption) => {
          const isTagOrphan =
            (state.all.filter(c => c.tags?.includes(String(tag.key)))?.length ?? 0) === 0;

          if (isTagOrphan) {
            const tgIndex = state.tags.findIndex(t => t.key === tag.key);
            state.tags.splice(tgIndex, 1);
          }
        });
      });
    },

    deleteConstructions: (state, { payload }: PayloadAction<string[]>) => {
      state.all = state.all.filter(c => !payload.includes(c.id));
      state.page = state.page.filter(c => !payload.includes(c.id));
      state.master = state.master.filter(c => !payload.includes(c.id));
    },

    update: (state, { payload }: PayloadAction<IPayloadConstructionStateUpdate[]>) => {
      state.all = updateStateCollections(payload, state.all);
      state.page = updateStateCollections(payload, state.page);
      state.master = updateStateCollections(payload, state.master);
    },

    loadLocationData: (
      state,
      {
        payload,
      }: PayloadAction<{
        locationCodes: ConstructionLocationCodes[];
        locationTypes: string[];
      }>,
    ) => {
      state.constructionLocationCodes = setToComboBoxOption(
        new Set(payload.locationCodes.map(lc => lc.name)),
      );
      state.constructionLocationTypes = setToComboBoxOption(
        new Set(payload.locationTypes),
      );
    },

    updateEntities: (state, {payload}: PayloadAction<ConstructionBulkEdit>) => {
      payload.ids.forEach(id => {
        const entity = state.page.find(p => p.id === id);

        if(entity) {
          if (payload.locationCode)
            entity.locationCode = payload.locationCode;
          if (payload.locationType)
            entity.locationType = payload.locationType;
          if (payload.phase) 
            entity.phase = payload.phase;
        }
      });
    },

  }
});


export const { load, update, select, changePage, search, addTags, removeTags, removeTagFromConstruction, deleteConstructions, loadLocationData, updateEntities, sort } = constructionsNewSlice.actions;
export default constructionsNewSlice.reducer;

